

import { cn } from "@sunfish/lib/utils";
import type { LucideProps } from "lucide-react";
import * as React from "react";
import { type ForwardRefExoticComponent, type RefAttributes } from "react";

export type StepItem<P> = {
  id?: string;
  title: string;
  description: string;
  icon: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  component?: React.ComponentType<P>;
  isCompleted?: boolean;
  [key: string]: unknown;
};

type StepperProps<P = unknown> = {
  steps: StepItem<P>[];
  initialIndex?: number;
  variant?: "line" | "circle" | "circle-alt";
  onChange?: (index: number) => void;
  activeIndex?: number;
};

export const Stepper = <P,>(props: StepperProps<P>) => {
  const { variant = "line", steps = [], activeIndex = 0 } = props;
  return (
    <div
      className={cn(
        "flex w-full flex-wrap gap-4 items-start",
        steps?.length === 1 ? "justify-end" : "justify-between",
      )}
    >
      {steps.map((step, index) => (
        <StepItemBlock
          key={step.id ?? step.title}
          {...step}
          isCompleted={
            step.isCompleted !== undefined
              ? step.isCompleted
              : index < activeIndex
          }
          isActive={activeIndex === index}
          variant={variant}
          onClick={() => props.onChange?.(index)}
        />
      ))}
    </div>
  );
};
Stepper.displayName = "Stepper";

const StepItemBlock = ({
  title,
  description,
  icon,
  isCompleted,
  isActive,
  variant,
  onClick,
}: {
  title: string;
  description: string;
  icon: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  isCompleted: boolean;
  isActive: boolean;
  variant: "line" | "circle" | "circle-alt";
  onClick: () => void;
}) => {
  return (
    <div
      className={cn(
        "flex items-center relative transition-all duration-200 justify-between flex-1",
        "after:transition-all after:duration-200",
        "after:content-[''] after:h-[2px] after:bg-border",
        "justify-start flex-col flex-1 after:relative after:order-[-1] after:start-[50%] after:end-[50%] after:top-[calc(40px/2)] after:w-[calc(100%-40px)]",
        "last:after:content-[''] last:after:w-0",
        isCompleted && "opacity-50",
        isActive && "opacity-100",
      )}
      data-variant={variant}
    >
      <div
        className={cn("flex-1 flex items-center flex-col justify-center gap-1")}
        onClick={onClick}
        onKeyUp={(e) => {
          if (e.key === "Enter") onClick();
        }}
      >
        <div
          className={cn(
            "bg-brand-100 text-brand-800 rounded-full p-3 opacity-70",
            isCompleted && "bg-green-400 text-foreground text-green-950",
            isActive && "bg-brand text-brand-foreground opacity-100",
          )}
        >
          {icon && React.createElement(icon, { size: 16 })}
        </div>
        <div
          className={cn(
            "flex flex-col items-center opacity-50",
            isActive && "text-blue opacity-100 dark:text-blue-100",
          )}
        >
          <span className="text-sm font-semibold min-w-max">{title}</span>
          {description && (
            <div className="text-xs text-center">{description}</div>
          )}
        </div>
      </div>
    </div>
  );
};
